import { convertToCommonErrors } from '../../common/utils/errorUtils.js';
import { TypeKeys, generateActionId } from './actionsUtils.js';
import type { DisplayItemsLoadAction, ErrorAction, ErrorActionCreator } from './actionsUtils.js';
import type {
  BillingAccount,
  BillingAccountSearchResponse,
  BillingAccountsResponse,
  GenericError,
} from '../../generated/api/models.js';

export interface LoadBillingAccountsAction
  extends DisplayItemsLoadAction<TypeKeys.LOAD_BILLING_ACCOUNTS, BillingAccount> {
  getAllItems?: boolean;
  sourceSystem?: string;
  forceLoad?: boolean;
}

export const loadBillingAccounts = (
  getAllItems?: boolean,
  sourceSystem?: string,
  forceLoad?: boolean
): LoadBillingAccountsAction => {
  return {
    getAllItems,
    id: generateActionId(),
    sourceSystem,
    forceLoad,
    reporting: false,
    limit: undefined,
    type: TypeKeys.LOAD_BILLING_ACCOUNTS,
  };
};

export interface LoadBillingAccountsFulfilledAction {
  billingAccounts?: BillingAccount[];
  searchResults?: BillingAccountSearchResponse[];
  useSearchService?: boolean;
  total: number;
  type: TypeKeys.LOAD_BILLING_ACCOUNTS_FULFILLED;
}

export const loadBillingAccountsFulfilled = (response: BillingAccountsResponse): LoadBillingAccountsFulfilledAction => {
  return {
    billingAccounts: response.billingAccounts,
    searchResults: response.searchResults,
    useSearchService: true,
    total: response.total,
    type: TypeKeys.LOAD_BILLING_ACCOUNTS_FULFILLED,
  };
};

export type LoadBillingAccountsFailedAction = ErrorAction<TypeKeys.LOAD_BILLING_ACCOUNTS_FAILED>;

export const loadBillingAccountsFailed: ErrorActionCreator<TypeKeys.LOAD_BILLING_ACCOUNTS_FAILED> = (
  message: string,
  status: number,
  errors?: GenericError[]
): LoadBillingAccountsFailedAction => {
  const commonErrors = convertToCommonErrors(message, status, errors);
  return {
    errors: commonErrors,
    type: TypeKeys.LOAD_BILLING_ACCOUNTS_FAILED,
  };
};

export interface UpsertBillingAccountScheduledChangeFulfilled {
  type: TypeKeys.UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FULFILLED;
}

export const upsertBillingAccountScheduledChangeFulfilled = (): UpsertBillingAccountScheduledChangeFulfilled => ({
  type: TypeKeys.UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FULFILLED,
});

export interface UpsertBillingAccountScheduledChangeFailed {
  type: TypeKeys.UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FAILED;
}

export const upsertBillingAccountScheduledChangeFailed = (): UpsertBillingAccountScheduledChangeFailed => ({
  type: TypeKeys.UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FAILED,
});

export interface PrepareBillingAccountEdit {
  type: TypeKeys.PREPARE_BILLING_ACCOUNT_EDIT;
}

export const prepareBillingAccountEdit = (): PrepareBillingAccountEdit => ({
  type: TypeKeys.PREPARE_BILLING_ACCOUNT_EDIT,
});

export type BillingAccountsActionTypes =
  | LoadBillingAccountsAction
  | LoadBillingAccountsFailedAction
  | LoadBillingAccountsFulfilledAction
  | PrepareBillingAccountEdit
  | UpsertBillingAccountScheduledChangeFulfilled
  | UpsertBillingAccountScheduledChangeFailed;
